import { z } from 'zod';

/*
 * Instagram carousel payload (Iter 18). This is the JSON contract the LLM must
 * return for `carousel` sessions. Limits mirror spec §3.2: char caps guarantee
 * the text fits the slide; word guidance lives in the prompt.
 */

// Extended_Pictographic covers emoji; FE0F = variation selector, 200D = ZWJ,
// 1F3FB-1F3FF = skin tone modifiers, 20E3 = keycap.
const EMOJI_RE = /[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\uFE0F\u200D\u20E3]/gu;
export function stripEmoji(s: string): string {
  return s
    .replace(EMOJI_RE, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([,.!?;:])/g, '$1')
    .trim();
}

const HIGHLIGHT_MAX = 60;
const bridgeField = z.string().trim().max(160).optional();
const highlightField = z.string().trim().max(HIGHLIGHT_MAX).optional();

export const carouselCoverSlideSchema = z.object({
  kind: z.literal('cover'),
  headline: z.string().trim().min(1).max(160),
  subline: z.string().trim().max(160).optional(),
  highlight: highlightField,
});

const BODY_TEXT_MAX = 600;

export const carouselBodySlideSchema = z.object({
  kind: z.literal('body'),
  text: z.string().trim().min(1).max(BODY_TEXT_MAX),
  highlight: highlightField,
  bridge: bridgeField,
});

export const carouselQuoteSlideSchema = z.object({
  kind: z.literal('quote'),
  text: z.string().trim().max(400).optional(),
  block: z.string().trim().min(1).max(320),
  highlight: highlightField,
  bridge: bridgeField,
});

export const carouselListSlideSchema = z.object({
  kind: z.literal('list'),
  text: z.string().trim().max(240).optional(),
  items: z.array(z.string().trim().min(1).max(90)).min(2).max(5),
  bridge: bridgeField,
});

export const carouselStatSlideSchema = z.object({
  kind: z.literal('stat'),
  value: z.string().trim().min(1).max(12),
  label: z.string().trim().min(1).max(60),
  text: z.string().trim().max(300).optional(),
  bridge: bridgeField,
});

export const carouselCtaSlideSchema = z.object({
  kind: z.literal('cta'),
  headline: z.string().trim().min(1).max(120),
  text: z.string().trim().min(1).max(300),
  highlight: highlightField,
});

export const carouselSlideSchema = z.discriminatedUnion('kind', [
  carouselCoverSlideSchema,
  carouselBodySlideSchema,
  carouselQuoteSlideSchema,
  carouselListSlideSchema,
  carouselStatSlideSchema,
  carouselCtaSlideSchema,
]);
export type CarouselSlide = z.infer<typeof carouselSlideSchema>;
export type CarouselSlideKind = CarouselSlide['kind'];

export const CAROUSEL_MIN_SLIDES = 6;
export const CAROUSEL_MAX_SLIDES = 20;

export const carouselPayloadSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    slides: z.array(carouselSlideSchema).min(CAROUSEL_MIN_SLIDES).max(CAROUSEL_MAX_SLIDES),
    caption: z.string().trim().min(1).max(2200),
  })
  .superRefine((p, ctx) => {
    if (p.slides[0]?.kind !== 'cover') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['slides', 0, 'kind'],
        message: 'first slide must be "cover"',
      });
    }
    const last = p.slides.length - 1;
    if (p.slides[last]?.kind !== 'cta') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['slides', last, 'kind'],
        message: 'last slide must be "cta"',
      });
    }
  });
export type CarouselPayload = z.infer<typeof carouselPayloadSchema>;

/** Which field a slide's `highlight` must be a substring of. */
function highlightTarget(slide: Record<string, unknown>): string | null {
  const kind = slide.kind;
  const pick = (k: string) => (typeof slide[k] === 'string' ? (slide[k] as string) : null);
  if (kind === 'cover') return pick('headline');
  if (kind === 'body') return pick('text');
  if (kind === 'quote') return pick('block');
  if (kind === 'cta') return [pick('headline'), pick('text')].filter(Boolean).join('\n');
  return null;
}

// Safety net: prompt instructs the model to use only `-`, but em/en dashes
// occasionally leak through. Normalize before the highlight-substring check
// (spec §3.3 step 2) so a highlight containing a dash still matches its
// (now-normalized) target field.
function normalizeDashes(s: string): string {
  return s.replace(/[—–]/g, '-');
}

function deepStripEmoji(value: unknown): unknown {
  if (typeof value === 'string') return normalizeDashes(stripEmoji(value));
  if (Array.isArray(value)) return value.map(deepStripEmoji);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepStripEmoji(v);
    }
    return out;
  }
  return value;
}

type RawSlide = Record<string, unknown>;

// What may sit between a bridge sentence and the text around it.
const TRAILING_PUNCT = /[\s.,:;!?…-]+$/u;
const LEADING_PUNCT = /^[\s.,:;!?…-]+/u;
const ENDS_SENTENCE = /([.!?…:;]\s*|\n\s*)$/u;
// A copy of the bridge is recognised at a word boundary, never inside a word. It is cut
// only where its sentence ends; if the next slide goes on with the same sentence
// («…делала скидки, работала…»), cutting would leave half a sentence, so the bridge goes.
const AT_WORD_BOUNDARY = /^([^\p{L}\p{N}]|$)/u;
const SENTENCE_ENDS_THERE = /^([.!?…:;]|\s*\n|\s*$)/u;

// Fields in the order a reader sees them above the next slide's bridge / below its own.
const LEADING_FIELDS: Record<string, string[]> = {
  body: ['text'],
  quote: ['text', 'block'],
  list: ['text'],
  stat: ['text'],
  cta: ['headline', 'text'],
};
const LAST_FIELD: Record<string, string> = { body: 'text', quote: 'block', stat: 'text' };

function isOptionalLeadIn(kind: unknown, field: string): boolean {
  return field === 'text' && (kind === 'quote' || kind === 'list' || kind === 'stat');
}

/**
 * The model sometimes leaves a bridge sentence in the text as well — at the end of its
 * own slide, at the start of the next one, or as a whole field further down the next
 * slide — so it is read twice in a row. Cut the copy; when that would empty a required
 * field, tear a sentence, or the copy is a whole field (a quote under its lead-in), drop
 * the bridge instead.
 */
function dedupeBridges(slides: RawSlide[]): void {
  slides.forEach((slide, i) => {
    if (typeof slide.bridge !== 'string') return;
    const core = slide.bridge.trim().replace(TRAILING_PUNCT, '');
    if (!core) return;
    // Case-insensitive: «и вот если всё сложить» repeats «И вот если всё сложить…».
    const lower = core.toLowerCase();

    const own = LAST_FIELD[slide.kind as string];
    const ownValue = own ? slide[own] : undefined;
    if (own && typeof ownValue === 'string') {
      const body = ownValue.replace(TRAILING_PUNCT, '');
      const before = body.slice(0, body.length - core.length);
      if (body.toLowerCase().endsWith(lower) && (!before.trim() || ENDS_SENTENCE.test(before))) {
        const rest = before.trimEnd();
        if (rest) slide[own] = rest;
        else if (isOptionalLeadIn(slide.kind, own)) delete slide[own];
        else {
          delete slide.bridge;
          return;
        }
      }
    }

    const next = slides[i + 1];
    if (!next) return;
    for (const field of LEADING_FIELDS[next.kind as string] ?? []) {
      const value = next[field];
      if (typeof value !== 'string') continue;
      const after = value.toLowerCase().startsWith(lower) ? value.slice(core.length) : null;
      if (after === null || !AT_WORD_BOUNDARY.test(after)) break;
      if (!SENTENCE_ENDS_THERE.test(after)) {
        delete slide.bridge; // the next slide continues this sentence: keep it whole there
        return;
      }
      const rest = after.replace(LEADING_PUNCT, '');
      if (rest) {
        next[field] = rest;
        return;
      }
      if (!isOptionalLeadIn(next.kind, field)) {
        delete slide.bridge;
        return;
      }
      delete next[field]; // the lead-in was only the bridge; check what leads now
    }
    const repeatsField = ['text', 'block', 'headline'].some((f) => {
      const value = next[f];
      return (
        typeof value === 'string' &&
        value.trim().replace(TRAILING_PUNCT, '').toLowerCase() === lower
      );
    });
    if (repeatsField) delete slide.bridge;
  });
}

function mergedBodyText(a: RawSlide, b: RawSlide): string {
  return [a.text, a.bridge, b.text]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join('\n\n');
}

/**
 * A long text can come back with more than CAROUSEL_MAX_SLIDES slides, which the
 * schema rejects. Merge the shortest pair of neighbouring body slides (the first one's
 * bridge stays between them as text) until the deck fits; stop when no pair fits one
 * body slide and let the schema report it.
 */
function mergeOverflow(slides: RawSlide[]): RawSlide[] {
  const out = [...slides];
  while (out.length > CAROUSEL_MAX_SLIDES) {
    let best = -1;
    let bestLength = Infinity;
    for (let i = 1; i + 1 < out.length - 1; i++) {
      const a = out[i]!;
      const b = out[i + 1]!;
      if (a.kind !== 'body' || b.kind !== 'body') continue;
      const length = mergedBodyText(a, b).length;
      if (length <= BODY_TEXT_MAX && length < bestLength) {
        best = i;
        bestLength = length;
      }
    }
    if (best === -1) break;
    const a = out[best]!;
    const b = out[best + 1]!;
    const merged: RawSlide = { kind: 'body', text: mergedBodyText(a, b) };
    const highlight = a.highlight ?? b.highlight;
    if (highlight !== undefined) merged.highlight = highlight;
    if (b.bridge !== undefined) merged.bridge = b.bridge;
    out.splice(best, 2, merged);
  }
  return out;
}

/**
 * Pre-validation cleanup of raw LLM output:
 *  1. strip emoji from every string (slides never carry emoji, see spec §3.2);
 *  2. cut a bridge sentence the model repeated on the next or its own slide;
 *  3. merge neighbouring body slides while there are more than CAROUSEL_MAX_SLIDES;
 *  4. drop `highlight` when it is not a substring of its target field or is too
 *     long: it is decoration and must not fail the whole deck.
 * Non-object input is returned as is so the schema reports the real error.
 */
export function normalizeCarouselPayload(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  // deepStripEmoji returns fresh objects, so the steps below may mutate them.
  const cleaned = deepStripEmoji(raw) as Record<string, unknown>;
  if (Array.isArray(cleaned.slides)) {
    let slides: unknown[] = cleaned.slides;
    if (slides.every((s) => s && typeof s === 'object' && !Array.isArray(s))) {
      dedupeBridges(slides as RawSlide[]);
      slides = mergeOverflow(slides as RawSlide[]);
    }
    cleaned.slides = slides.map((s) => {
      if (!s || typeof s !== 'object') return s;
      const slide = s as RawSlide;
      if (typeof slide.highlight === 'string') {
        const target = highlightTarget(slide);
        const length = slide.highlight.trim().length;
        if (
          !target ||
          length === 0 ||
          length > HIGHLIGHT_MAX ||
          !target.includes(slide.highlight)
        ) {
          delete slide.highlight;
        }
      }
      return slide;
    });
  }
  return cleaned;
}

/* --- Wire DTOs ------------------------------------------------------------ */

export const carouselRenderDtoSchema = z.object({
  style_code: z.string(),
  image_urls: z.array(z.string()),
});
export type CarouselRenderDto = z.infer<typeof carouselRenderDtoSchema>;

export const carouselStyleDtoSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string(),
  preview_url: z.string().nullable(),
});
export type CarouselStyleDto = z.infer<typeof carouselStyleDtoSchema>;

export const carouselStylesResponseSchema = z.object({
  styles: z.array(carouselStyleDtoSchema),
});
export type CarouselStylesResponse = z.infer<typeof carouselStylesResponseSchema>;

// Instagram handles: letters, digits, dots, underscores, max 30. Leading @ is tolerated.
export const instagramNickSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/^@+/, ''))
  .pipe(z.string().regex(/^[A-Za-z0-9._]{1,30}$/, 'nick must be an Instagram handle'));

// PATCH /sessions/:id/style
export const sessionStyleBodySchema = z.object({
  style_code: z.string().min(1).max(32),
  nick: instagramNickSchema,
});
export type SessionStyleBody = z.infer<typeof sessionStyleBodySchema>;

export const sessionStyleResponseSchema = z.object({
  style_code: z.string(),
  nick: z.string(),
});
export type SessionStyleResponse = z.infer<typeof sessionStyleResponseSchema>;

// POST /sessions/:id/render
export const sessionRenderBodySchema = z.object({
  style_code: z.string().min(1).max(32),
});
export type SessionRenderBody = z.infer<typeof sessionRenderBodySchema>;

export const sessionRenderResponseSchema = z.object({
  render: carouselRenderDtoSchema,
});
export type SessionRenderResponse = z.infer<typeof sessionRenderResponseSchema>;

// POST /sessions/:id/deliver
export const sessionDeliverResponseSchema = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
});
export type SessionDeliverResponse = z.infer<typeof sessionDeliverResponseSchema>;
